import { Box, Stack } from "@mui/material";
import { Loader } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { useGetAnalysisGoals } from "Infrastructure/ApiZfp/AnalyzerApi";
import { type GoalOverview, GoalOverviewLifecycleStatus } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { useContext, useState } from "react";
import { EditGoalDrawer } from "../GoalManagement/EditGoalDrawer";
import { ArchivedGoals } from "./ArchivedGoals";
import { EmptyTimeline } from "./EmptyTimeline";
import { GoalTimelineRow } from "./GoalTimelineRow";
import { GoalsSortToggle } from "./GoalsSortToggle";
import { GoalsTimelineAxis } from "./GoalsTimelineAxis";
import { HorizonDivider } from "./HorizonDivider";
import { TodayMarker } from "./TodayMarker";
import { type SortOrder, type TimelineItem, buildTimelineItems, sortByTargetDate } from "./utils/buildTimelineItems";

type Props = {
  onAddGoal: () => void;
};

export const GoalsTimeline = ({ onAddGoal }: Props) => {
  const { analysisId, versionId, readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;
  const { data: goalsList, isPending } = useGetAnalysisGoals(analysisId, versionId);

  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [editedGoal, setEditedGoal] = useState<GoalOverview | null>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  if (isPending) {
    return <Loader />;
  }

  if (!goalsList) {
    return null;
  }

  const items = buildTimelineItems(goalsList, new Date(), sortOrder);
  const archivedGoals = sortByTargetDate(
    goalsList.goals.filter((goal) => goal.lifecycleStatus === GoalOverviewLifecycleStatus.archived),
    sortOrder,
  );

  const openEditDrawer = (goal: GoalOverview) => {
    setEditedGoal(goal);
    setIsEditDrawerOpen(true);
  };

  const renderItem = (item: TimelineItem) => {
    switch (item.type) {
      case "today":
        return <TodayMarker key="today" />;
      case "horizon":
        return <HorizonDivider key={item.horizon} horizon={item.horizon} startDate={item.startDate} isReversed={sortOrder === "desc"} />;
      case "goal":
        return <GoalTimelineRow key={item.goal.guid} goal={item.goal} onEdit={openEditDrawer} />;
    }
  };

  return (
    <Stack gap={Spacing.XL}>
      {items.length > 0 ? (
        <>
          <GoalsSortToggle value={sortOrder} onChange={setSortOrder} />
          <Box position="relative">
            <GoalsTimelineAxis />
            {/* positioned, so the rows paint above the axis line */}
            <Box position="relative">{items.map(renderItem)}</Box>
          </Box>
        </>
      ) : (
        <EmptyTimeline readOnly={readOnly} onAddGoal={onAddGoal} />
      )}

      <ArchivedGoals goals={archivedGoals} onEdit={openEditDrawer} />

      {/* Keyed per goal so the drawer's internal goal-type state resets, and unmounted after the exit
          animation so its detail queries don't stay active (e.g. refetching a goal that was just deleted). */}
      {editedGoal && (
        <EditGoalDrawer
          key={editedGoal.guid}
          open={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          goal={editedGoal}
          onExited={() => setEditedGoal(null)}
        />
      )}
    </Stack>
  );
};
